import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useRef, useEffect, useState } from 'react';
import { SocialMedia } from '../../constants/links';
import BannerProps from '../../model/banner-props';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import HeroVisuals from '../../components/HeroVisuals';

// Calculate years of experience
function getYearsOfExperience(): string {
  const startDate = new Date('2019-06-01');
  const now = new Date();
  const years = (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24 * 365.25);
  return years.toFixed(1);
}

export default function Banner({ isMobile }: BannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [yearsExp] = useState(getYearsOfExperience());
  
  // Mouse parallax for the hero text
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  
  const springConfig = { damping: 25, stiffness: 150 };
  const textX = useSpring(useTransform(mouseX, [-1, 1], [-30, 30]), springConfig);
  const textY = useSpring(useTransform(mouseY, [-1, 1], [-20, 20]), springConfig);
  const textX2 = useSpring(useTransform(mouseX, [-1, 1], [20, -20]), springConfig);
  const textY2 = useSpring(useTransform(mouseY, [-1, 1], [15, -15]), springConfig);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width * 2 - 1;
      const y = (e.clientY - rect.top) / rect.height * 2 - 1;
      mouseX.set(x);
      mouseY.set(y);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (container) {
        container.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, [mouseX, mouseY]);

  return (
    <Box
      ref={containerRef}
      component="section"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 14, md: 10 },
        pb: { xs: 8, md: 4 },
      }}
    >
      {/* Ghost text background */}
      <motion.div
        style={{
          position: 'absolute',
          left: '-5%',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 0,
          pointerEvents: 'none',
          x: textX2,
          y: textY2,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '25vw', md: '20vw' },
            fontFamily: 'Bebas Neue, sans-serif',
            color: 'transparent',
            WebkitTextStroke: '1px rgba(68, 212, 130, 0.06)',
            lineHeight: 0.85,
            userSelect: 'none',
          }}
        >
          DEV
        </Typography>
      </motion.div>

      <motion.div
        style={{
          position: 'absolute',
          right: '-5%',
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 0,
          pointerEvents: 'none',
          x: textX,
          y: textY,
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '25vw', md: '20vw' },
            fontFamily: 'Bebas Neue, sans-serif',
            color: 'transparent',
            WebkitTextStroke: '1px rgba(68, 212, 130, 0.06)',
            lineHeight: 0.85,
            userSelect: 'none',
          }}
        >
          CODE
        </Typography>
      </motion.div>

      <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left side - Text content */}
          <Grid item xs={12} md={6} lg={5}>
            {/* Terminal-style label */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  padding: '8px 16px',
                  background: 'rgba(68, 212, 130, 0.1)',
                  border: '1px solid rgba(68, 212, 130, 0.2)',
                  borderRadius: '8px',
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: '#44D482',
                    animation: 'pulse 2s infinite',
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: 'Fira Code, monospace',
                    fontSize: '0.85rem',
                    color: '#44D482',
                    letterSpacing: '0.05em',
                  }}
                >
                  SOFTWARE ENGINEER
                </Typography>
              </Box>
            </motion.div>

            {/* Main heading with parallax */}
            <motion.div
              style={{ x: textX, y: textY }}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', sm: '4rem', md: '5rem', lg: '6rem' },
                  fontFamily: 'Bebas Neue, sans-serif',
                  color: '#fff',
                  lineHeight: 0.95,
                  mb: 1,
                }}
              >
                ABHISHEK
              </Typography>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', sm: '4rem', md: '5rem', lg: '6rem' },
                  fontFamily: 'Bebas Neue, sans-serif',
                  background: 'linear-gradient(135deg, #44D482 0%, #6EEAA3 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 0.95,
                  mb: 3,
                }}
              >
                KUMAR
              </Typography>
            </motion.div>

            {/* Tagline */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <Typography
                variant="h5"
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 400,
                  maxWidth: '500px',
                  mb: 1.5,
                  fontSize: { xs: '1rem', md: '1.1rem' },
                }}
              >
                Truth can only be found in one place: the code
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  maxWidth: '450px',
                  mb: 3,
                  fontSize: { xs: '0.9rem', md: '0.95rem' },
                }}
              >
                I design and code simple things beautifully, and I love what I do.
              </Typography>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <Button
                variant="outlined"
                href={SocialMedia.MAIL_TO}
                endIcon={<ArrowOutwardIcon />}
                sx={{
                  borderColor: '#44D482',
                  color: '#44D482',
                  padding: '12px 28px',
                  fontSize: '0.95rem',
                  borderRadius: '12px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: '#44D482',
                    color: '#0A0A0F',
                    borderColor: '#44D482',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(68, 212, 130, 0.3)',
                  },
                }}
              >
                Let's Talk
              </Button>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: { xs: 4, md: 5 },
                  mt: 4,
                  flexWrap: 'wrap',
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Bebas Neue, sans-serif',
                      fontSize: { xs: '2.5rem', md: '3rem' },
                      color: '#44D482',
                      lineHeight: 1,
                    }}
                  >
                    {yearsExp}+
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Fira Code, monospace',
                      fontSize: '0.7rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Years Experience
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Bebas Neue, sans-serif',
                      fontSize: { xs: '2.5rem', md: '3rem' },
                      color: '#44D482',
                      lineHeight: 1,
                    }}
                  >
                    9+
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: 'Fira Code, monospace',
                      fontSize: '0.7rem',
                      color: 'rgba(255, 255, 255, 0.5)',
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                    }}
                  >
                    Projects Completed
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          </Grid>

          {/* Right side - Visual elements */}
          <Grid 
            item 
            xs={12} 
            md={6} 
            lg={7}
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              alignItems: 'center',
            }}
          >
            <HeroVisuals />
          </Grid>
        </Grid>
      </Container>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        style={{
          position: 'absolute',
          bottom: '40px',
          left: '50%',
          transform: 'translateX(-50%)',
        }}
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <Box
            sx={{
              width: '24px',
              height: '40px',
              border: '2px solid rgba(68, 212, 130, 0.3)',
              borderRadius: '12px',
              display: 'flex',
              justifyContent: 'center',
              pt: '8px',
            }}
          >
            <Box
              sx={{
                width: '4px',
                height: '8px',
                background: '#44D482',
                borderRadius: '2px',
              }}
            />
          </Box>
        </motion.div>
      </motion.div>
    </Box>
  );
}
